import { resendService } from './resendService';
import { emailTemplates } from './emailTemplates';
import { supabase } from './supabaseService';

// Envío inmediato de emails transaccionales vía Resend.
// La cola `email_queue` + digests diarios se retiraron 2026-09-18
// (queue nunca se procesaba, digests nunca se llegaron a programar).

class EmailNotificationService {
  private readonly baseUrl = window.location.origin;

  private async sendImmediateEmail(
    to: string,
    subject: string,
    html: string,
    text: string
  ): Promise<void> {
    if (!resendService.isConfigured()) {
      console.warn('Resend no configurado, email no enviado');
      return;
    }
    await resendService.sendEmail({ from: '', to, subject, html, text });
  }

  private async getUserEmailPreferences(userId: string): Promise<{
    receive_daily_digest: boolean;
    receive_approval_emails: boolean;
    receive_invoice_emails: boolean;
    receive_admin_notifications: boolean;
  }> {
    const defaults = {
      receive_daily_digest: true,
      receive_approval_emails: true,
      receive_invoice_emails: true,
      receive_admin_notifications: true,
    };
    try {
      const { data } = await supabase
        .from('email_preferences')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();
      return { ...defaults, ...(data || {}) };
    } catch {
      return defaults;
    }
  }

  async sendNewUserWelcomeEmail(
    userName: string,
    userEmail: string,
    temporaryPassword: string
  ): Promise<void> {
    const resetLink = `${this.baseUrl}/reset-password`;
    const template = emailTemplates.newUserWelcome(userName, userEmail, temporaryPassword, resetLink);
    await this.sendImmediateEmail(userEmail, template.subject, template.html, template.text);
  }

  async sendTimesheetApprovedEmail(
    collaboratorName: string,
    collaboratorEmail: string,
    collaboratorId: string,
    projectName: string,
    hours: number,
    amount: number,
    approvedBy: string,
    approvedAt: Date,
    timesheetId: string
  ): Promise<void> {
    const prefs = await this.getUserEmailPreferences(collaboratorId);
    if (!prefs.receive_approval_emails) return;

    const viewUrl = `${this.baseUrl}/pagos?tab=timesheets&id=${timesheetId}`;
    const template = emailTemplates.timesheetApproved(
      collaboratorName,
      projectName,
      hours,
      amount,
      approvedBy,
      approvedAt,
      viewUrl
    );
    await this.sendImmediateEmail(collaboratorEmail, template.subject, template.html, template.text);
  }

  async sendTimesheetRejectedEmail(
    collaboratorName: string,
    collaboratorEmail: string,
    collaboratorId: string,
    projectName: string,
    hours: number,
    rejectedBy: string,
    rejectedAt: Date,
    reason: string,
    timesheetId: string
  ): Promise<void> {
    const prefs = await this.getUserEmailPreferences(collaboratorId);
    if (!prefs.receive_approval_emails) return;

    const viewUrl = `${this.baseUrl}/pagos?tab=timesheets&id=${timesheetId}`;
    const template = emailTemplates.timesheetRejected(
      collaboratorName,
      projectName,
      hours,
      rejectedBy,
      rejectedAt,
      reason,
      viewUrl
    );
    await this.sendImmediateEmail(collaboratorEmail, template.subject, template.html, template.text);
  }

  async sendInvoiceValidatedEmail(
    collaboratorName: string,
    collaboratorEmail: string,
    collaboratorId: string,
    invoiceNumber: string,
    amount: number,
    currency: string,
    validatedBy: string,
    validatedAt: Date,
    invoiceId: string,
    downloadUrl?: string
  ): Promise<void> {
    const prefs = await this.getUserEmailPreferences(collaboratorId);
    if (!prefs.receive_invoice_emails) return;

    const viewUrl = `${this.baseUrl}/pagos?tab=invoices&id=${invoiceId}`;
    const template = emailTemplates.invoiceValidated(
      collaboratorName,
      invoiceNumber,
      amount,
      currency,
      validatedBy,
      validatedAt,
      viewUrl,
      downloadUrl
    );
    await this.sendImmediateEmail(collaboratorEmail, template.subject, template.html, template.text);
  }

  async sendInvoiceRejectedEmail(
    collaboratorName: string,
    collaboratorEmail: string,
    collaboratorId: string,
    invoiceNumber: string,
    amount: number,
    currency: string,
    reason: string,
    rejectedBy: string,
    invoiceId: string
  ): Promise<void> {
    const prefs = await this.getUserEmailPreferences(collaboratorId);
    if (!prefs.receive_invoice_emails) return;

    const viewUrl = `${this.baseUrl}/pagos?tab=invoices&id=${invoiceId}`;
    const template = emailTemplates.invoiceRejected(
      collaboratorName,
      invoiceNumber,
      amount,
      currency,
      reason,
      rejectedBy,
      viewUrl
    );
    await this.sendImmediateEmail(collaboratorEmail, template.subject, template.html, template.text);
  }

  async sendInvoicePaidEmail(
    collaboratorName: string,
    collaboratorEmail: string,
    collaboratorId: string,
    invoiceNumber: string,
    amount: number,
    currency: string,
    paidAt: Date,
    paymentMethod: string,
    invoiceId: string
  ): Promise<void> {
    const prefs = await this.getUserEmailPreferences(collaboratorId);
    if (!prefs.receive_invoice_emails) return;

    const viewUrl = `${this.baseUrl}/pagos?tab=invoices&id=${invoiceId}`;
    const template = emailTemplates.invoicePaid(
      collaboratorName,
      invoiceNumber,
      amount,
      currency,
      paidAt,
      paymentMethod,
      viewUrl
    );
    await this.sendImmediateEmail(collaboratorEmail, template.subject, template.html, template.text);
  }

  async sendProjectAssignedEmail(
    collaboratorName: string,
    collaboratorEmail: string,
    projectName: string,
    clientName: string,
    viewUrl: string
  ): Promise<void> {
    const template = emailTemplates.projectAssigned(collaboratorName, projectName, clientName, viewUrl);
    await this.sendImmediateEmail(collaboratorEmail, template.subject, template.html, template.text);
  }

  async sendTaskStatusChangedEmail(
    collaboratorName: string,
    collaboratorEmail: string,
    taskTitle: string,
    projectName: string,
    prevStatus: string,
    newStatus: string,
    viewUrl: string
  ): Promise<void> {
    const template = emailTemplates.taskStatusChanged(
      collaboratorName, taskTitle, projectName, prevStatus, newStatus, viewUrl
    );
    await this.sendImmediateEmail(collaboratorEmail, template.subject, template.html, template.text);
  }
}

export const emailNotificationService = new EmailNotificationService();
